import React, { useState, useEffect, useRef } from "react";

import {
	ListSubheader,
	IconButton,
	Divider,
	Typography,
	Toolbar,
	Input,
	Button,
	FormHelperText,
	CircularProgress,
	Switch,
	Theme,
	makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import EditIcon from "@material-ui/icons/Edit";
import CalendarToday from "@material-ui/icons/CalendarToday";

import { BuilderAsset } from "../../../../src/entities/BuilderAsset";
import { ServerResponse } from "../../../services/server/OldWebClient";
import classNames from "classnames";
import { BuilderAssetService } from "../../../../src/services/BuilderAssetService";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import OfflineIcon from "@material-ui/icons/CheckCircle";
import OnlineIcon from "@material-ui/icons/CheckCircleOutline";

import { useOfflineAssets } from "../../offline/useOfflineAssets";
import { useBuilder } from "../useBuilder";
import { FieldValidationError, ValidationHelper } from "../../../services/server/ServerResponses";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import DateHelper from "../../../utilities/DateHelper";

const useStyles = makeStyles((theme: Theme) => ({
	root: {},
	toolbar: theme.mixins.toolbar,
	title: {
		paddingTop: 10,
		paddingBottom: 7,
		textAlign: "left",
		fontSize: 22,
	},
	subtitle: {
		paddingTop: theme.spacing(1),
		textAlign: "left",
		fontSize: 19,
	},
	closeIcon: {
		float: "right",
	},
	searchBar: {
		minHeight: "48px",
		maxHeight: "48px",
		width: "95%",
		backgroundColor: "transparent",
		borderRadius: "0",
		margin: "0 auto",
		marginTop: 10,
	},
	inputSearchText: {
		padding: "15px",
	},
	placeHolderIcon: {
		marginTop: "30px",
		width: "50px",
		height: "50px",
		color: "#CCC",
	},
	placeHolderText: {
		fontFamily: "Roboto, Helvetica",
		fontSize: "16px",
		marginTop: "12px",
		color: "#5f6368",
		textAlign: "center",
	},
	loading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1),
		marginLeft: "auto",
		marginRight: "auto",
		display: "block",
	},
	searchResults: {
		overflowY: "auto",
	},
	bibleCopyright: {
		padding: 5,
		paddingLeft: 16,
		paddingRight: 16,
		"& a": {
			color: theme.palette.primary.main,
		},
	},
	copyIcon: {
		display: "none",
		right: 0,
		top: "30%",
		"&:hover": {
			display: "block",
		},
		[theme.breakpoints.down("lg")]: {
			display: "block",
		},
	},
	result: {
		whiteSpace: "normal",
		height: "auto",
		"&:hover": {
			"&+div": {
				display: "block",
			},
		},
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	copyInProgress: {
		margin: theme.spacing(1),
	},
	starIcon: {
		width: 13,
		height: 13,
	},
	chevronLeft: {
		marginRight: "2px",
		width: "48px",
		height: "48px",
		color: "#FFF",
		"&:hover": {
			backgroundColor: "rgb(145, 145, 145)",
		},
	},
	verticalText: {
		transform: "rotate(-90deg)",
		width: "90px",
		transformOrigin: "45% 85%",
		textAlign: "center",
		lineHeight: "52px",
	},
	grow: {
		flex: 1,
	},
	infoContainer: {
		fontFamily: "Roboto, Helvetica",
		fontSize: "13px",
		color: "#202124",
		padding: "0 0 0 24px",
	},
	inlineHolder: {
		float: "right",
	},
	inlineIcon: {
		color: "rgb(143, 143, 143)",
		fontSize: "16px",
		verticalAlign: "text-bottom",
		float: "right",
		marginRight: "6px",
		cursor: "pointer",
	},
	infoRow: {
		lineHeight: "20px",
		padding: "6px 0",
		display: "flex",
	},
	infoProp: {
		flex: "0 0 100px",
		color: "#5f6368",
	},
	infoValue: {
		boxSizing: "border-box",
		flex: "1 1 auto",
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontWeight: "bolder",
	},
	drawerInfoContent: {
		padding: "0 6px 0 6px",
	},
	infoClickable: {
		cursor: "pointer",
		color: "#1967d2",
	},
	editInput: {
		fontSize: "12px",
		width: "244px",
		marginBottom: "4px",
	},
	editActionsContainer: {
		textAlign: "right",
		paddingRight: "16px",
	},
	dateInput: {
		"& div:first-of-type": {
			"& div:first-of-type": {
				fontSize: "12px",
				lineHeight: "4px",
			},
		},
	},
	drawerTitle: {
		maxWidth: "86&",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
}));

interface Props {
	onDrawerToggle: (open: boolean) => void;
	asset?: BuilderAsset;
}

interface EditState {
	preachDate: Date | null;
	location: string;
	scriptures: string;
	name: string;
	templateDescription?: string;
}

export function DetailsContent(props: Props) {
	const { asset, onDrawerToggle } = props;
	const classes = useStyles();
	const [inEditMode, setInEditMode] = useState<boolean>(false);
	const [errors, setErrors] = useState<FieldValidationError[]>([]);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [editingTitleInDrawer, setEditingTitleInDrawer] = useState<boolean>(false);
	const [showCalendar, setShowCalendar] = useState<boolean>(false);
	const [editState, setEditState] = useState<EditState | undefined>();
	const [editStateUnsaved, setEditStateUnsaved] = useState<EditState | undefined>();

	const { setAssetOfflineMode } = useOfflineAssets();
	const { setBuilderAsset } = useBuilder();
	const calendarRef = useRef<any>(null);

	useEffect(() => {
		if (asset) {
			setEditState({
				location: asset.locationPreached,
				preachDate: asset.datePreached ?? null,
				scriptures: asset.scripture,
				name: asset.name,
				templateDescription: asset.templateDescription,
			});
			setEditStateUnsaved({
				location: asset.locationPreached,
				preachDate: asset.datePreached ?? null,
				scriptures: asset.scripture,
				name: asset.name,
				templateDescription: asset.templateDescription,
			});
		}
	}, [asset]);

	useEffect(() => {
		if (editStateUnsaved && asset) {
			if (editStateUnsaved.name !== asset.name) {
				const updatedEditState: EditState = {
					...editStateUnsaved,
					name: asset.name,
				};
				setEditStateUnsaved(updatedEditState);
			}
		}
	}, [asset]);

	useEffect(() => {
		const showCalendarCard = setTimeout(function () {
			calendarRef.current.setState({ calendarShow: true });
		}, 100);
		if (!showCalendar) {
			clearTimeout(showCalendarCard);
		}
	}, [showCalendar]);

	const handleDrawerToggle = (open: boolean) => {
		onDrawerToggle(open);
	};

	const handleEditClose = () => {
		if (!inEditMode) {
			setInEditMode(true);
			return;
		}

		if (editState) {
			setInEditMode(false);
			setEditStateUnsaved({
				location: editState.location,
				preachDate: editState.preachDate,
				scriptures: editState.scriptures,
				name: editState.name,
				templateDescription: editState.templateDescription,
			});
		}
	};

	const handleSaveAsset = async (e: React.FormEvent<HTMLElement>) => {
		e.preventDefault();
		save();
	};

	const toggleStar = () => {
		if (asset) {
			save(!asset.isStarred);
		}
	};

	const save = async (starChangedTo?: boolean) => {
		setIsSaving(true);

		let templateDescription = "";

		if (editStateUnsaved!.templateDescription) {
			templateDescription = editStateUnsaved!.templateDescription!;
		}

		const updatedAsset: BuilderAsset = {
			...asset!,
			locationPreached: editStateUnsaved!.location,
			scripture: editStateUnsaved!.scriptures,
			datePreached: editStateUnsaved!.preachDate ?? undefined,
			isStarred: starChangedTo !== undefined ? starChangedTo : asset!.isStarred,
			name: editStateUnsaved!.name,
			templateDescription: templateDescription,
		};

		let response = await BuilderAssetService.save(updatedAsset);
		if (ServerResponse.isSuccess<BuilderAsset>(response)) {
			setIsSaving(false);
			setInEditMode(false);
			setEditingTitleInDrawer(false);

			const updatedAsset = response.data;
			setEditState({
				location: updatedAsset.locationPreached,
				preachDate: updatedAsset.datePreached ?? null,
				scriptures: updatedAsset.scripture,
				name: updatedAsset.name,
				templateDescription: updatedAsset.templateDescription,
			});
			setEditStateUnsaved({
				location: updatedAsset.locationPreached,
				preachDate: updatedAsset.datePreached ?? null,
				scriptures: updatedAsset.scripture,
				name: updatedAsset.name,
				templateDescription: updatedAsset.templateDescription,
			});
			setBuilderAsset(updatedAsset);

			// BAD, better check these values, they can be null

			// if (editStateUnsaved!.scriptures.length !== editState!.scriptures.length) {
			//     EventTracking.Builder.onSaveAsset(EventTracking.Builder.EntityDetails.Scripture);
			// }
			// else if (editStateUnsaved!.location.length !== editState!.location.length) {
			//     EventTracking.Builder.onSaveAsset(EventTracking.Builder.EntityDetails.Location);
			// }

			// if (editStateUnsaved!.preachDate && editState!.preachDate) {
			//     const detaildate = editState!.preachDate!.setHours(0, 0, 0, 0);
			//     const altereddate = editStateUnsaved!.preachDate!.setHours(0, 0, 0, 0);
			//     if (detaildate !== altereddate) {
			//         EventTracking.Builder.onSaveAsset(EventTracking.Builder.EntityDetails.PreachDate);
			//     }
			// }
			// else if (editState!.preachDate && !editStateUnsaved!.preachDate) {
			//     EventTracking.Builder.onSaveAsset(EventTracking.Builder.EntityDetails.PreachDate);
			// }
			// else if (!editState!.preachDate && editStateUnsaved!.preachDate) {
			//     EventTracking.Builder.onSaveAsset(EventTracking.Builder.EntityDetails.PreachDate);
			// }
		} else if (ServerResponse.isModelValidation(response)) {
			setErrors(response.errors);
			setIsSaving(false);
		} else if (ServerResponse.isError(response)) {
			setIsSaving(false);
		}
	};

	// const onChangeDate = (date: Date) => {
	// 	if (editStateUnsaved) {
	// 		setEditStateUnsaved({ ...editStateUnsaved, preachDate: date });
	// 		setShowCalendar(false);
	// 	}
	// };

	// const setUndefinedState = () => {
	// 	if (editStateUnsaved) {
	// 		setEditStateUnsaved({ ...editStateUnsaved, preachDate: undefined });
	// 		setShowCalendar(false);
	// 	}
	// };

	const onAddPreachDate = (e: any) => {
		e.preventDefault();
		if (editStateUnsaved) {
			setEditStateUnsaved({ ...editStateUnsaved });
			setShowCalendar(true);
		}
	};

	const toggleOfflineAsset = async (location: "Drawer Toggle (Builder)" | "Drawer Icon (Builder)") => {
		if (!asset) {
			return;
		}

		setIsSaving(true);
		const response = await setAssetOfflineMode(asset.id, !asset.offlineMode, location);
		if (response) {
			setBuilderAsset(response);
		}
		setIsSaving(false);
	};

	if (isSaving) {
		return (
			<React.Fragment>
				<div style={{ textAlign: "center" }}>
					<CircularProgress style={{ marginTop: "50px" }} />
					<br />
					<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
						Saving Details...
					</Typography>
				</div>
			</React.Fragment>
		);
	}

	return (
		<>
			<React.Fragment>
				<ListSubheader className={classes.title}>
					{editingTitleInDrawer ? (
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<div>
								<Input
									error={ValidationHelper.isFieldInError(errors, "Name")}
									placeholder="Title"
									value={editStateUnsaved!.name}
									onKeyDown={(e) => {
										if (e.keyCode === 13) {
											save();
										}
									}}
									onChange={(e: any) =>
										editStateUnsaved ? setEditStateUnsaved({ ...editStateUnsaved, name: e.currentTarget.value }) : null
									}
									inputProps={{
										"aria-label": "Name",
									}}
								/>
								{ValidationHelper.isFieldInError(errors, "Name") && (
									<FormHelperText error id="location-error-text">
										<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, "Name") }} />
									</FormHelperText>
								)}
							</div>

							<IconButton onClick={() => save()} className={classes.closeIcon}>
								<CheckIcon />
							</IconButton>
							<IconButton
								onClick={() => {
									setEditingTitleInDrawer(false);
									setEditStateUnsaved({ ...editStateUnsaved!, name: asset!.name });
								}}
								className={classes.closeIcon}
							>
								<CloseIcon />
							</IconButton>
						</div>
					) : (
						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<span style={{ cursor: "pointer" }} className={classes.drawerTitle} onClick={() => setEditingTitleInDrawer(true)}>
								{asset!.name}
							</span>
							<IconButton onClick={() => handleDrawerToggle(false)} className={classes.closeIcon}>
								<CloseIcon />
							</IconButton>
						</div>
					)}
				</ListSubheader>
				<Divider />

				{asset && (
					<div className={classes.drawerInfoContent}>
						<Toolbar>
							<Typography variant="subtitle1">{asset.offlineMode ? "Offline Enabled" : "Offline Disabled"}</Typography>
							<Switch color="primary" onChange={() => toggleOfflineAsset("Drawer Toggle (Builder)")} checked={asset.offlineMode} />
						</Toolbar>
						<Toolbar>
							<Typography variant="subtitle1">{inEditMode ? "Edit Details" : "Details"}</Typography>
							{!inEditMode && (
								<IconButton onClick={handleEditClose}>
									<EditIcon fontSize="small" />
								</IconButton>
							)}
						</Toolbar>
						<div className={classes.infoContainer}>
							{!inEditMode && (
								<>
									<div className={classes.infoRow}>
										<div className={classes.infoProp}>
											Title
											<div className={classes.inlineHolder}>
												{asset.offlineMode ? (
													<OfflineIcon
														onClick={() => toggleOfflineAsset("Drawer Icon (Builder)")}
														className={classes.inlineIcon}
													/>
												) : (
													<OnlineIcon
														onClick={() => toggleOfflineAsset("Drawer Icon (Builder)")}
														className={classes.inlineIcon}
													/>
												)}
											</div>
											<div className={classes.inlineHolder} onClick={() => toggleStar()}>
												{asset.isStarred ? (
													<StarIcon className={classes.inlineIcon} />
												) : (
													<StarBorderIcon className={classes.inlineIcon} />
												)}
											</div>
										</div>
										<div className={classNames(classes.infoValue, classes.infoClickable)} onClick={() => setInEditMode(true)}>
											{asset.name}
										</div>
									</div>
									{asset.isTemplate && (
										<div className={classes.infoRow}>
											<div className={classes.infoProp}>Description</div>
											<div className={classNames(classes.infoValue, classes.infoClickable)} onClick={() => setInEditMode(true)}>
												{asset.templateDescription}
											</div>
										</div>
									)}
									{!asset.isTemplate && (
										<>
											<div className={classes.infoRow}>
												<div className={classes.infoProp}>Preach Date</div>
												<div
													className={classNames(classes.infoValue, classes.infoClickable)}
													onClick={() => setInEditMode(true)}
												>
													{asset.datePreached ? DateHelper.buildDateFormat(asset.datePreached) : "Not Set"}
												</div>
											</div>
											<div className={classes.infoRow}>
												<div className={classes.infoProp}>Location</div>
												<div
													className={classNames(classes.infoValue, classes.infoClickable)}
													onClick={() => setInEditMode(true)}
												>
													{asset.locationPreached}
												</div>
											</div>
											<div className={classes.infoRow}>
												<div className={classes.infoProp}>Scriptures</div>
												<div
													className={classNames(classes.infoValue, classes.infoClickable)}
													onClick={() => setInEditMode(true)}
												>
													{asset.scripture}
												</div>
											</div>
										</>
									)}
								</>
							)}
							<form onSubmit={handleSaveAsset}>
								{inEditMode && (
									<>
										<Input
											error={ValidationHelper.isFieldInError(errors, "Name")}
											placeholder="Title"
											className={classes.editInput}
											value={editStateUnsaved!.name}
											onChange={(e: any) =>
												editStateUnsaved ? setEditStateUnsaved({ ...editStateUnsaved, name: e.currentTarget.value }) : null
											}
											inputProps={{
												"aria-label": "Name",
											}}
										/>
										{ValidationHelper.isFieldInError(errors, "Name") && (
											<FormHelperText error id="location-error-text">
												<span
													dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, "Name") }}
												/>
											</FormHelperText>
										)}
										{asset.isTemplate && (
											<>
												<Input
													error={ValidationHelper.isFieldInError(errors, "TemplateDescription")}
													placeholder="Description"
													className={classes.editInput}
													value={editStateUnsaved!.templateDescription}
													onChange={(e: any) =>
														editStateUnsaved
															? setEditStateUnsaved({ ...editStateUnsaved, templateDescription: e.currentTarget.value })
															: null
													}
													inputProps={{
														"aria-label": "TemplateDescription",
													}}
												/>
												{ValidationHelper.isFieldInError(errors, "TemplateDescription") && (
													<FormHelperText error id="location-error-text">
														<span
															dangerouslySetInnerHTML={{
																__html: ValidationHelper.getFieldErrorSummary(errors, "TemplateDescription"),
															}}
														/>
													</FormHelperText>
												)}
											</>
										)}
										{!asset.isTemplate && (
											<>
												<MuiPickersUtilsProvider utils={DateFnsUtils}>
													<DatePicker
														style={{ width: 244 }}
														name="date-input"
														placeholder="Preach Date"
														clearable
														value={editStateUnsaved?.preachDate ?? null}
														onChange={(date) => editStateUnsaved && setEditStateUnsaved({...editStateUnsaved, preachDate: date ? new Date(date.setHours(0,0,0,0)) : null})}
														InputProps={{
															endAdornment: <CalendarToday style={{color: "rgba(0, 0, 0, 0.54)", fontSize: 20}} />
														}}
														inputProps={{
															style: { fontSize: 12 },
															value: editStateUnsaved?.preachDate?.toLocaleDateString([], { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' }) ?? "",
														}}
													/>
												</MuiPickersUtilsProvider>
												<Input
													error={ValidationHelper.isFieldInError(errors, "LocationPreached")}
													placeholder="Location"
													className={classes.editInput}
													value={editStateUnsaved!.location}
													onChange={(e: any) =>
														editStateUnsaved
															? setEditStateUnsaved({ ...editStateUnsaved, location: e.currentTarget.value })
															: null
													}
													inputProps={{
														"aria-label": "Location",
													}}
												/>
												{ValidationHelper.isFieldInError(errors, "LocationPreached") && (
													<FormHelperText error id="location-error-text">
														<span
															dangerouslySetInnerHTML={{
																__html: ValidationHelper.getFieldErrorSummary(errors, "LocationPreached"),
															}}
														/>
													</FormHelperText>
												)}
												<Input
													error={ValidationHelper.isFieldInError(errors, "ScriptureReferences")}
													placeholder="Scripture"
													className={classes.editInput}
													value={editStateUnsaved!.scriptures}
													onChange={(e: any) =>
														editStateUnsaved
															? setEditStateUnsaved({ ...editStateUnsaved, scriptures: e.currentTarget.value })
															: null
													}
													inputProps={{
														"aria-label": "Scripture",
													}}
												/>
												{ValidationHelper.isFieldInError(errors, "ScriptureReferences") && (
													<FormHelperText error id="location-error-text">
														<span
															dangerouslySetInnerHTML={{
																__html: ValidationHelper.getFieldErrorSummary(errors, "ScriptureReferences"),
															}}
														/>
													</FormHelperText>
												)}
											</>
										)}
									</>
								)}
								<div className={classes.infoRow}>
									<div className={classes.infoProp}>Modified</div>
									<div className={classes.infoValue}>{DateHelper.buildDateFormat(asset.dateModified)}</div>
								</div>
								<div className={classes.infoRow}>
									<div className={classes.infoProp}>Access</div>
									<div className={classes.infoValue}>{DateHelper.buildDateFormat(asset.dateAccessed)}</div>
								</div>
								<div className={classes.infoRow}>
									<div className={classes.infoProp}>Created</div>
									<div className={classes.infoValue}>{DateHelper.buildDateFormat(asset.dateCreated)}</div>
								</div>
								{inEditMode && (
									<div className={classes.editActionsContainer}>
										<Button onClick={handleEditClose} color="primary">
											Cancel
										</Button>
										<Button type="submit" color="primary" disabled={isSaving}>
											Save
										</Button>
									</div>
								)}
							</form>
						</div>
					</div>
				)}
			</React.Fragment>
		</>
	);
}